import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { withApollo, compose } from 'react-apollo';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  Button,
  FormGroup,
} from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import CommonFormComponent from '../../CommonField/CommonFormComponent';
import AdminAsyncAutocomplete from '../AdminAsyncAutocomplete';
import { generateTimes } from '../../../helpers/formatting';
import createComplimentaryReservation from '../../../actions/siteadmin/createComplimentaryReservation';
import messages from '../../../locale/messages';
import validate from './validate';
import listingsQuery from './listingsQuery.graphql';
import usersQuery from './usersQuery.graphql';

import s from './CreateReservationModal.css';
import cp from '../../../components/commonStyle.css';

class CreateReservationForm extends Component {
  static propTypes = {
    createReservationModal: PropTypes.bool,
    client: PropTypes.object.isRequired,
    createComplimentaryReservation: PropTypes.func.isRequired,
  };

  loadListingOptions = async (searchList) => {
    const { client } = this.props;
    const { data } = await client.query({
      query: listingsQuery,
      variables: {
        currentPage: 1,
        searchList: searchList || '',
      },
      fetchPolicy: 'network-only',
    });

    return (data?.getAllListings?.usersData || []).map((item) => ({
      value: item.id,
      label: item.title
        ? `${item.title}${item.city ? ` (${item.city})` : ''} — #${item.id}`
        : `Car #${item.id}`,
    }));
  };

  loadUserOptions = async (searchList) => {
    const { client } = this.props;
    const { data } = await client.query({
      query: usersQuery,
      variables: {
        currentPage: 1,
        searchList: searchList || '',
      },
      fetchPolicy: 'network-only',
    });

    return (data?.userManagement?.usersData || []).map((item) => {
      const name = [item.profile?.firstName, item.profile?.lastName]
        .filter(Boolean)
        .join(' ');
      return {
        value: item.id,
        label: [name, item.email].filter(Boolean).join(' — ') || item.id,
      };
    });
  };

  handleFormSubmit = async (values) => {
    const { createComplimentaryReservation } = this.props;
    await createComplimentaryReservation(values);
  };

  render() {
    const { handleSubmit, submitting } = this.props;
    const { formatMessage } = this.props.intl;
    const timeLookup = generateTimes(0, 1410);

    return (
      <div className={cx(s.modalRoot, 'adminstyle')}>
        <form onSubmit={handleSubmit(this.handleFormSubmit)}>
          <FormGroup className={s.fieldRow}>
            <label className={cp.labelTextNew}>
              <FormattedMessage {...messages.carNameLabel} />
            </label>
            <Field
              name="listId"
              component={AdminAsyncAutocomplete}
              loadOptions={this.loadListingOptions}
              placeholder={formatMessage(messages.searchCarPlaceholder)}
              inputClass={cp.formControlInput}
            />
          </FormGroup>

          <FormGroup className={s.fieldRow}>
            <label className={cp.labelTextNew}>
              <FormattedMessage {...messages.renterName} />
            </label>
            <Field
              name="guestId"
              component={AdminAsyncAutocomplete}
              loadOptions={this.loadUserOptions}
              placeholder={formatMessage(messages.searchRenterPlaceholder)}
              inputClass={cp.formControlInput}
            />
          </FormGroup>

          <div className={cx(s.dateGrid, s.fieldRow)}>
            <FormGroup>
              <label className={cp.labelTextNew}>
                <FormattedMessage {...messages.checkInDateLabel} />
              </label>
              <Field
                name="checkIn"
                type="date"
                inputClass={cp.formControlInput}
                component={CommonFormComponent}
              />
            </FormGroup>
            <FormGroup>
              <label className={cp.labelTextNew}>
                <FormattedMessage {...messages.checkOutDateLabel} />
              </label>
              <Field
                name="checkOut"
                type="date"
                inputClass={cp.formControlInput}
                component={CommonFormComponent}
              />
            </FormGroup>
          </div>

          <div className={cx(s.timeGrid, s.fieldRow)}>
            <FormGroup>
              <label className={cp.labelTextNew}>
                <FormattedMessage {...messages.tripStart} />
              </label>
              <Field
                name="startTime"
                inputClass={cx(cp.formControlSelect, 'commonFormSelectRTL')}
                component={CommonFormComponent}
              >
                <option value="">{formatMessage(messages.selectLabel)}</option>
                {timeLookup.map((item) => (
                  <option value={item.value} key={`start-${item.value}`}>{item.label}</option>
                ))}
              </Field>
            </FormGroup>
            <FormGroup>
              <label className={cp.labelTextNew}>
                <FormattedMessage {...messages.tripEnd} />
              </label>
              <Field
                name="endTime"
                inputClass={cx(cp.formControlSelect, 'commonFormSelectRTL')}
                component={CommonFormComponent}
              >
                <option value="">{formatMessage(messages.selectLabel)}</option>
                {timeLookup.map((item) => (
                  <option value={item.value} key={`end-${item.value}`}>{item.label}</option>
                ))}
              </Field>
            </FormGroup>
          </div>

          <FormGroup className={s.fieldRow}>
            <label className={cp.labelTextNew}>
              <FormattedMessage {...messages.reservationNotesLabel} />
            </label>
            <Field
              name="reservationNotes"
              componentClass="textarea"
              inputClass={cp.formControlInput}
              component={CommonFormComponent}
              label={formatMessage(messages.reservationNotesPlaceholder)}
            />
          </FormGroup>

          <FormGroup className={s.fieldRow}>
            <div className={cx(cp.textAlignRight, 'textAlignLeftRTL')}>
              <Button
                type="submit"
                disabled={submitting}
                className={cx(cp.btnPrimary, cp.btnlarge)}
              >
                <FormattedMessage {...messages.createLabel} />
              </Button>
            </div>
          </FormGroup>
        </form>
      </div>
    );
  }
}

CreateReservationForm = reduxForm({
  form: 'CreateReservationForm',
  validate,
})(CreateReservationForm);

const mapState = (state) => ({
  createReservationModal: state.adminModalStatus.createReservationModal,
});

const mapDispatch = {
  createComplimentaryReservation,
};

export default compose(
  injectIntl,
  withStyles(s, cp),
  withApollo,
  connect(mapState, mapDispatch),
)(CreateReservationForm);
